import React, { useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { LuRefreshCw } from "react-icons/lu";
import { EmailPreview } from "./api";

interface Props {
  preview: EmailPreview | null;
  loading?: boolean;
  onRefresh?: () => void;
  // Shown when there is nothing to preview yet (e.g. unsaved new record)
  emptyLabel?: string;
}

// Renders the email exactly as the customer will receive it. The HTML comes
// from the server already wrapped in the customer layout with the unsubscribe
// footer, so it goes into a sandboxed iframe — never into the page — so its
// styles cannot leak into the dashboard and nothing inside it can run.
const EmailPreviewPane = ({ preview, loading, onRefresh, emptyLabel }: Props) => {
  const [mode, setMode] = useState<"html" | "text">("html");

  const previewingAs = preview?.variables?.first_name;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h3 className="text-[16px] font-semibold">Email Preview</h3>
          <p className="text-[12px] text-gray-500">
            {previewingAs
              ? `Previewing as ${previewingAs} — sample values shown`
              : "How this email will look to customers"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setMode("html")}
              className={`px-3 py-1 text-[12px] ${
                mode === "html" ? "bg-[#0979A1] text-white" : "bg-white text-gray-600"
              }`}>
              HTML
            </button>
            <button
              type="button"
              onClick={() => setMode("text")}
              className={`px-3 py-1 text-[12px] ${
                mode === "text" ? "bg-[#0979A1] text-white" : "bg-white text-gray-600"
              }`}>
              Plain text
            </button>
          </div>
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              title="Refresh preview"
              className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50">
              <LuRefreshCw size={14} />
            </button>
          )}
        </div>
      </div>

      {preview?.subject && (
        <div className="mb-3 border border-gray-100 rounded-lg px-3 py-2 bg-gray-50">
          <p className="text-[11px] text-gray-500">Subject</p>
          <p className="text-[13px] font-medium break-words">{preview.subject}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-[420px]">
          <TailSpin color="skyblue" />
        </div>
      ) : !preview ? (
        <div className="flex justify-center items-center h-[420px] border border-dashed border-gray-200 rounded-lg">
          <p className="text-gray-500 text-[13px] text-center px-6">
            {emptyLabel || "Save the draft to generate a preview."}
          </p>
        </div>
      ) : mode === "html" ? (
        <iframe
          title="Email preview"
          sandbox=""
          srcDoc={preview.html}
          className="w-full h-[420px] border border-gray-200 rounded-lg bg-white"
        />
      ) : (
        <pre className="w-full h-[420px] overflow-auto border border-gray-200 rounded-lg bg-gray-50 p-3 text-[12px] whitespace-pre-wrap font-mono">
          {preview.text}
        </pre>
      )}

      <p className="text-[11px] text-gray-400 mt-2">
        The preview reads the saved version, and its unsubscribe link is inert.
      </p>
    </div>
  );
};

export default EmailPreviewPane;
